// Inleveranser → Returer (owner 2026-11-04, mockup admin-v1/returns, "allt ok som det ser ut"): the customers'
// return orders — who, which warehouse, how far, the deviations, the reports to the ERP and the ERP's lock — and a
// page per return with the lines as in the ERP beside what came back, what Trail knows of each batch, the lines the
// door created, the pallets, the office's text, every report and the deviations.

import { Fragment, useEffect, useState } from "react";
import { Admin, Api, type ExpectedReceiptDetail, type ExpectedReceiptSummary, type Qty, type ReceiptProgress, type ReturnFacts } from "../api/client.js";
import { fmtQty, t } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";
import { selectedSiteId } from "./Sites.jsx";

const fmtT = (iso: string) => new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
const sumQ = (q: Qty[], uom: string) => q.filter((x) => x.uom === uom).reduce((a, x) => a + Math.abs(Number(x.value)), 0);
const devLabel = (d: string) => (d === "return-other-customer" ? t("adm.ret.dev.other") : d === "return-unknown-batch" ? t("adm.ret.dev.unknown") : d === "manual-entry" ? t("adm.ret.dev.manual") : d);
const repPill = (s: string) => (s === "sent" ? "ok" : s === "failed" ? "bad" : s === "shadow" ? "warn" : "mut");

export function Returns({ filter }: { filter: "open" | "closed" }) {
  const [rows, setRows] = useState<ExpectedReceiptSummary[] | null>(null);
  const [facts, setFacts] = useState<Record<string, { progress: ReceiptProgress | null; facts: ReturnFacts | null }>>({});
  const [open, setOpen] = useState<string | null>(null);
  useDetailHistory(!!open, () => setOpen(null));

  useEffect(() => {
    setRows(null);
    Admin.receipts(filter === "open" ? "open,proposed" : "closed,cancelled", selectedSiteId() ?? "all", "return").then(async (r) => {
      setRows(r);
      // Few returns at a time: the list shows how far each has come and its deviations.
      const all = await Promise.all(r.map(async (x) => [x.id, { progress: await Api.progress(x.id).catch(() => null), facts: await Admin.returnFacts(x.id).catch(() => null) }] as const));
      setFacts(Object.fromEntries(all));
    }).catch(() => setRows([]));
  }, [filter]);

  if (open) return <ReturnPage id={open} summary={rows?.find((r) => r.id === open) ?? null} onBack={() => setOpen(null)} />;

  return (
    <div className="card">
      <div className="bd flush">
        <table className="at" style={{ tableLayout: "fixed" }}>
          <colgroup><col style={{ width: 104 }} /><col style={{ width: 210 }} /><col style={{ width: 64 }} /><col style={{ width: 180 }} /><col /><col style={{ width: 140 }} /><col style={{ width: 132 }} /></colgroup>
          <thead><tr><th>{t("adm.ret.order")}</th><th>{t("adm.ret.customer")}</th><th>{t("adm.ret.warehouse")}</th><th>{t("adm.ret.back")}</th><th>{t("adm.ret.deviations")}</th><th>{t("adm.ret.erp")}</th><th>{t("adm.col.status")}</th></tr></thead>
          <tbody>
            {rows?.length === 0 && <tr><td colSpan={7} className="mut">{t("adm.ret.none")}</td></tr>}
            {rows?.map((r) => {
              const f = facts[r.id];
              const p = f?.progress;
              const done = p ? p.lines.filter((l) => sumQ(l.received, l.expected.uom) >= Math.abs(Number(l.expected.value)) && Number(l.expected.value) !== 0).length : 0;
              const lines = p ? p.lines.length : r.lines;
              const pct = lines ? Math.round((100 * done) / lines) : 0;
              const devs = f?.facts?.deviations ?? [];
              const reps = f?.facts?.reports ?? [];
              const sent = reps.filter((x) => x.state === "sent").length;
              const failed = reps.filter((x) => x.state === "failed").length;
              return (
                <tr key={r.id} className="click" onClick={() => setOpen(r.id)}>
                  <td className="id">{r.reference}</td>
                  <td>{r.customer?.name ?? "—"}{f?.facts?.customerKey ? <div className="mut" style={{ fontSize: 12.5 }}>{t("adm.ret.customerKey", { key: f.facts.customerKey })}</div> : null}</td>
                  <td className="id">{r.erpWarehouse ?? "—"}</td>
                  <td>
                    <div className="bar" style={{ height: 8, borderRadius: 999, background: "var(--panel-2)", overflow: "hidden", marginTop: 4 }}><i style={{ display: "block", height: "100%", width: `${pct}%`, background: "var(--accent)" }} /></div>
                    <div className="mut" style={{ fontSize: 12.5 }}>{p ? (p.containers ? t("adm.ret.progress", { done, lines, n: p.containers, pallets: p.pallets }) : t("adm.ret.nothingYet", { lines })) : ""}</div>
                  </td>
                  <td style={{ whiteSpace: "normal" }}>
                    {devs.length === 0 ? <span className="mut">—</span> : devs.slice(0, 2).map((d, i) => (
                      <div key={i}><span className={`pill ${d.detail === "return-other-customer" ? "bad" : "warn"}`}>{devLabel(d.detail)}</span><div style={{ fontSize: 12.5, color: "var(--warn)" }}>{d.reason ?? ""}</div></div>
                    ))}
                  </td>
                  <td>{failed ? <span className="pill bad">{t("adm.ret.repFailed", { n: failed })}</span> : sent ? <span className="pill ok">{t("adm.ret.repSent", { n: sent })}</span> : <span className="pill mut">{t("adm.ret.repNone")}</span>}</td>
                  <td>{r.erpLock ? <span className="pill bad">{t("erp.lockedTag")}</span> : <span className={`pill ${r.status === "closed" ? "ok" : r.status === "open" ? "warn" : "mut"}`}>{t(`adm.del.status.${r.status}` as "adm.del.status.open")}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReturnPage({ id, summary, onBack }: { id: string; summary: ExpectedReceiptSummary | null; onBack: () => void }) {
  const [detail, setDetail] = useState<ExpectedReceiptDetail | null>(null);
  const [progress, setProgress] = useState<ReceiptProgress | null>(null);
  const [facts, setFacts] = useState<ReturnFacts | null>(null);
  useEffect(() => {
    Api.getReceipt(id).then(setDetail).catch(() => setDetail(null));
    Api.progress(id).then(setProgress).catch(() => setProgress(null));
    Admin.returnFacts(id).then(setFacts).catch(() => setFacts(null));
  }, [id]);
  const customer = detail?.customer?.name ?? summary?.customer?.name ?? "";
  const lotsOf = (productId: string | null) => (facts?.lots ?? []).filter((l) => l.productId === productId);
  const historyLine = (h: ReturnFacts["lots"][number]["history"], lot: string | null) => {
    if (!h) return lot ? t("adm.ret.batch", { lot }) : "";
    if (h.kind === "unknown") return t("adm.ret.unknownSince", { lot: lot ?? "?", since: h.since ?? "?" });
    return t(h.sameCustomer ? "adm.ret.soldSame" : "adm.ret.soldOther", { lot: lot ?? "?", date: h.to.at?.slice(0, 10) ?? "?", order: h.to.orderRef ?? "?", customer: h.to.name ?? h.to.key ?? "?" });
  };
  return (
    <>
      <DetailBar title={t("adm.ret.title", { ref: detail?.reference ?? summary?.reference ?? "" })} sub={[customer, facts?.customerKey ? t("adm.ret.customerKey", { key: facts.customerKey }) : null, detail?.erpWarehouse ? t("adm.ret.whHead", { w: detail.erpWarehouse }) : null].filter(Boolean).join(" · ")} onBack={onBack} />
      <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 16, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card">
            <div className="hd">{t("adm.del.lines")} <span className="sub">{t("adm.ret.linesSub")}</span></div>
            <div className="bd flush">
              <table className="at" style={{ tableLayout: "fixed" }}>
                <colgroup><col style={{ width: 40 }} /><col /><col style={{ width: 50 }} /><col style={{ width: 104 }} /><col style={{ width: 104 }} /><col style={{ width: 120 }} /></colgroup>
                <thead><tr><th>{t("adm.ret.line")}</th><th>{t("adm.ret.article")}</th><th>{t("adm.ret.warehouse")}</th><th className="num">{t("adm.ret.expected")}</th><th className="num">{t("adm.ret.back")}</th><th>{t("adm.ret.price")}</th></tr></thead>
                <tbody>
                  {(detail?.lineItems ?? []).map((l) => {
                    const p = progress?.lines.find((x) => x.lineNo === l.lineNo);
                    const back = p ? sumQ(p.received, l.uom) : 0;
                    const door = l.createdBy === "trail";
                    // The price Trail set on a line it created, as the report noted it ("rad 2 (ny): …, pris … (källa)").
                    const note = (facts?.reports ?? []).flatMap((x) => x.notes).find((n) => n.startsWith(`rad ${l.lineNo} (ny)`));
                    const priceText = note ? note.split(", ").slice(1).join(", ") : null;
                    return (
                      <tr key={l.lineNo}>
                        <td className="id">{l.lineNo}</td>
                        <td style={{ whiteSpace: "normal" }}>{l.sku} {l.productName ?? l.description ?? ""}{door && <span className="pill mut" style={{ marginLeft: 6 }}>{t("adm.ret.door")}</span>}
                          {lotsOf(l.productId).map((x, i) => <div key={i} style={{ fontSize: 12.5, color: x.history?.kind === "unknown" || (x.history?.kind === "sold" && !x.history.sameCustomer) ? "var(--warn)" : "var(--muted)" }}>{historyLine(x.history, x.lot)}</div>)}
                        </td>
                        <td className="id">{l.erpWarehouse ?? detail?.erpWarehouse ?? "—"}</td>
                        <td className="num">{door || Number(l.quantity) === 0 ? "—" : fmtQty(Math.abs(Number(l.quantity)), l.uom)}</td>
                        <td className="num">{fmtQty(back, l.uom)}</td>
                        <td style={{ whiteSpace: "normal" }}>{door ? <span className="mut" style={{ fontSize: 12.5 }}>{priceText ?? "—"}</span> : <span className="mut">{t("adm.ret.officeLine")}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card">
            <div className="hd">{t("adm.del.pallets")} <span className="sub">{progress ? t("adm.ret.palletsSub", { pallets: progress.pallets, n: progress.containers }) : ""}</span></div>
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>SSCC</th><th className="num">Kolli</th><th>{t("adm.st.location")}</th><th>{t("adm.col.who")}</th></tr></thead>
                <tbody>
                  {progress?.palletList.length === 0 && <tr><td colSpan={4} className="mut">—</td></tr>}
                  {progress?.palletList.map((p) => <tr key={p.containerId}><td className="id">{p.sscc ?? p.containerId}</td><td className="num">{p.children}</td><td className="id">{p.locationCode ?? "—"}</td><td>{p.actorId.replace(/^user:/, "").slice(0, 12)} · {fmtT(p.at)}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card"><div className="hd">{t("adm.ret.office")}</div>
            <div className="bd">{(detail?.notes ?? summary?.notes ?? []).length === 0 ? <p className="ahint">{t("adm.ret.noNotes")}</p> : (detail?.notes ?? summary?.notes ?? []).map((n, i) => <p key={i} className="ahint" style={{ borderLeft: "3px solid var(--line)", paddingLeft: 10, fontStyle: "italic", margin: "0 0 6px" }}>{n}</p>)}</div>
          </div>
          <div className="card"><div className="hd">{t("adm.ret.toErp")} <span className="sub">{t("adm.ret.toErpSub")}</span></div>
            <div className="bd flush">
              <table className="at" style={{ tableLayout: "fixed" }}>
                <colgroup><col style={{ width: 58 }} /><col /><col style={{ width: 98 }} /></colgroup>
                <thead><tr><th>{t("adm.ret.when")}</th><th>{t("adm.ret.what")}</th><th>{t("adm.ret.state")}</th></tr></thead>
                <tbody>
                  {(facts?.reports ?? []).length === 0 && <tr><td colSpan={3} className="mut">—</td></tr>}
                  {(facts?.reports ?? []).map((x) => (
                    <tr key={x.id}>
                      <td className="id">{fmtTime(x.createdAt)}</td>
                      <td style={{ whiteSpace: "normal" }}>{x.ref.replace(/^\S+\s/, t("adm.ret.pallet") + " ").replace(/ \+\w+$/, ` · ${t("adm.ret.refill")}`)}
                        {x.lines.map((l, i) => <div key={i} className="mut" style={{ fontSize: 12.5 }}>{l.createdBy === "trail" ? t("adm.ret.newLine", { n: l.lineNo }) : t("adm.ret.lineN", { n: l.lineNo })}: {l.sku} {l.received} {l.uom}{l.lots.length ? `, ${l.lots.join(", ")}` : ""}{x.rows.filter((r) => l.lots.includes(r.lot)).map((r) => ` → ${t("adm.ret.row", { n: r.shipmentNo })}`).join("")}</div>)}
                        {x.error && <div style={{ fontSize: 12.5, color: "var(--bad)" }}>{x.error.slice(0, 160)}</div>}
                      </td>
                      <td><span className={`pill ${repPill(x.state)}`}>{t(`adm.set.erpReport.st.${x.state}` as "adm.set.erpReport.st.pending")}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card"><div className="hd">{t("adm.ret.deviations")} <span className="sub">{(facts?.deviations ?? []).length}</span></div>
            <div className="bd">
              {(facts?.deviations ?? []).length === 0 && <p className="ahint">—</p>}
              <dl className="kv" style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: "6px 12px", margin: 0 }}>
                {(facts?.deviations ?? []).map((d, i) => <Fragment key={i}><dt className="mut">{devLabel(d.detail)}</dt><dd style={{ margin: 0 }}>{d.reason ?? d.lot ?? ""} <span className="mut">· {fmtTime(d.at)}</span></dd></Fragment>)}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
